import type { FlowDefinition } from '../types';

export const BLANK_SECTION_FLOW_PRESET_ID = 'blank-section-logic-flow';

function createFlowEntityId(prefix: string): string {
  const generatedId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return `${prefix}-${generatedId}`;
}

export function findSectionLogicFlow(
  flows: FlowDefinition[],
  sectionId: string | null | undefined
): FlowDefinition | null {
  if (!sectionId) return null;
  return flows.find((flow) => flow.scope === 'section' && flow.sectionId === sectionId) ?? null;
}

export function findPreferredSectionLogicFlow(
  flows: FlowDefinition[],
  args: {
    sectionId: string | null | undefined;
    blockIds?: Iterable<string> | null;
  }
): FlowDefinition | null {
  if (!args.sectionId) return null;

  const scopedFlows = flows.filter(
    (flow) => flow.scope === 'section' && flow.sectionId === args.sectionId
  );
  const blockIds = new Set(args.blockIds ?? []);

  if (blockIds.size > 0) {
    const scopedFlowWithBlocks =
      scopedFlows.find((flow) => flow.nodes.some((node) => blockIds.has(node.blockId))) ?? null;
    if (scopedFlowWithBlocks) return scopedFlowWithBlocks;

    const legacyFlowWithBlocks =
      flows.find((flow) => flow.nodes.some((node) => blockIds.has(node.blockId))) ?? null;
    if (legacyFlowWithBlocks) return legacyFlowWithBlocks;
  }

  const populatedScopedFlow =
    scopedFlows.find(
      (flow) => flow.nodes.length > 0 || flow.connections.length > 0 || flow.fields.length > 0
    ) ?? null;
  if (populatedScopedFlow) return populatedScopedFlow;

  return scopedFlows[0] ?? null;
}

export function upsertBlankSectionFlow(
  flows: FlowDefinition[],
  args: {
    sectionId: string;
    name?: string;
    presetId?: string;
  }
): FlowDefinition[] {
  if (!args.sectionId) return flows;
  if (findSectionLogicFlow(flows, args.sectionId)) return flows;

  return [
    ...flows,
    {
      id: createFlowEntityId('flow'),
      name: args.name ?? 'Section Logic Flow',
      scope: 'section',
      sectionId: args.sectionId,
      presetId: args.presetId ?? BLANK_SECTION_FLOW_PRESET_ID,
      fields: [],
      nodes: [],
      connections: [],
    },
  ];
}
